import { Link } from 'react-router-dom';
import { MetricCard } from '@/components/ui/index.js';
import { useFmt } from '@/lib/useFmt.js';
import type { OrdersResponse } from '@/lib/types.js';
import {
  OrderTable,
  SectionEmpty,
  SectionPager,
  SectionTitle,
  channelLabel,
  planLabel,
  type SectionPaging,
} from './shared.js';

/**
 * 国内订单 section — 微信 / 支付宝 / iOS IAP. Source is the `orders`
 * table; empty until app-store / WeChat-pay integration ships.
 */
export function CnyOrdersSection({
  data,
  offset,
  onOffsetChange,
  pageSize,
}: { data: OrdersResponse['real_revenue'] } & SectionPaging): JSX.Element {
  const { fmtDateTime } = useFmt();
  const wechatCny = (data.by_channel['wechat'] ?? 0) / 100;
  const alipayCny = (data.by_channel['alipay'] ?? 0) / 100;
  return (
    <section className="mb-8">
      <SectionTitle
        icon="💰"
        title="国内订单（微信 / 支付宝 / iOS IAP）"
        emptyHint={data.order_count === 0 ? '暂无 · 应用内付费上线后才会有' : undefined}
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <MetricCard
          label="总收入"
          value={`¥${data.total_yuan.toFixed(2)}`}
          tone={data.total_cents > 0 ? 'success' : 'neutral'}
        />
        <MetricCard
          label="付款订单数"
          value={data.order_count}
          tone={data.order_count > 0 ? 'info' : 'neutral'}
        />
        <MetricCard label="微信支付" value={`¥${wechatCny.toFixed(2)}`} tone="neutral" />
        <MetricCard label="支付宝" value={`¥${alipayCny.toFixed(2)}`} tone="neutral" />
      </div>

      {data.orders.length === 0 ? (
        <SectionEmpty>
          还没有真实付款订单。
          <br />
          等微信 / 支付宝商户号下来 + 应用内付费上线后，这里会显示每一笔交易。
        </SectionEmpty>
      ) : (
        <OrderTable>
          <thead className="bg-white/[0.04]">
            <tr>
              <th className="px-4 py-3 text-left text-slate-300 font-medium">用户</th>
              <th className="px-4 py-3 text-left text-slate-300 font-medium">套餐</th>
              <th className="px-4 py-3 text-left text-slate-300 font-medium">渠道</th>
              <th className="px-4 py-3 text-left text-slate-300 font-medium">金额</th>
              <th className="px-4 py-3 text-left text-slate-300 font-medium">订单号</th>
              <th className="px-4 py-3 text-left text-slate-300 font-medium">付款时间</th>
            </tr>
          </thead>
          <tbody>
            {data.orders.map((o) => (
              <tr key={o.id} className="border-t border-white/[0.08]">
                <td className="px-4 py-3">
                  <Link to={`/users/${o.user_id}`} className="text-shark-300 hover:text-shark-200">
                    {o.user_email ?? o.user_id.slice(0, 8)}
                  </Link>
                </td>
                <td className="px-4 py-3 text-slate-300">{planLabel(o.plan)}</td>
                <td className="px-4 py-3 text-slate-300">{channelLabel(o.channel)}</td>
                <td className="px-4 py-3 text-shark-300 font-semibold">¥{o.amount_yuan.toFixed(2)}</td>
                <td className="px-4 py-3 font-mono text-xs text-slate-400">{o.id.slice(0, 12)}</td>
                <td className="px-4 py-3 text-slate-400">{fmtDateTime(o.paid_at)}</td>
              </tr>
            ))}
          </tbody>
        </OrderTable>
      )}

      <SectionPager total={data.order_count} paging={{ offset, onOffsetChange, pageSize }} />
    </section>
  );
}
